/**
 * Routing (§12, §6).
 *
 * Two guards, and the distinction is deliberate:
 *
 *   `RequireAuth` keeps an anonymous visitor out of the child's app.
 *   `RequireProfile` sends an authenticated child with no profile to setup,
 *   because §48 says class and board are needed before any curriculum-
 *   dependent screen can show anything real.
 *
 * Neither is security. §42: "Trust frontend authorization" is on the list of
 * things the frontend engineer must NOT do — the backend refuses, and these
 * guards only save the child from seeing a broken screen on the way.
 *
 * Every page is lazy (§52: route-level code splitting), so a child on a slow
 * connection downloads Home, not Home plus Practice plus Parent.
 */

import { lazy, Suspense, type ReactNode } from 'react';
import { Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';

import { useAuth } from '@/features/auth/AuthProvider';
import { LEGACY_MAGIC_LINK_PATH, MAGIC_LINK_PATH } from '@/features/auth/magicLink';
import { useProfile } from '@/hooks/useProfile';
import { CardSkeleton, LoadingAnnouncement } from '@/components/ui/states';

const WelcomePage = lazy(() => import('@/pages/auth/WelcomePage'));
const ExchangePage = lazy(() => import('@/pages/auth/ExchangePage'));
const ProfileSetupPage = lazy(() => import('@/pages/auth/ProfileSetupPage'));
const HomePage = lazy(() => import('@/pages/home/HomePage'));
const HomeworkPage = lazy(() => import('@/pages/homework/HomeworkPage'));
const HomeworkSessionPage = lazy(() => import('@/pages/homework/HomeworkSessionPage'));
const LearnPage = lazy(() => import('@/pages/learn/LearnPage'));
const LearnSkillPage = lazy(() => import('@/pages/learn/LearnSkillPage'));
const PracticePage = lazy(() => import('@/pages/practice/PracticePage'));
const PracticeSessionPage = lazy(() => import('@/pages/practice/PracticeSessionPage'));
const ProfilePage = lazy(() => import('@/pages/profile/ProfilePage'));
const ProgressPage = lazy(() => import('@/pages/profile/ProgressPage'));
const StartPage = lazy(() => import('@/pages/start/StartPage'));
const SubscribePage = lazy(() => import('@/pages/billing/SubscribePage'));
const ParentSetupPage = lazy(() => import('@/pages/parent/ParentSetupPage'));
const AddChildPage = lazy(() => import('@/pages/parent/AddChildPage'));
const ChildProgressPage = lazy(() => import('@/pages/progress/ChildProgressPage'));
const PrivacyPage = lazy(() => import('@/pages/legal/PrivacyPage'));
const TermsPage = lazy(() => import('@/pages/legal/TermsPage'));
const RefundsPage = lazy(() => import('@/pages/legal/RefundsPage'));
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'));

function RouteFallback() {
  return (
    <div className="app-content flex flex-col gap-3 pt-[var(--space-8)]">
      <LoadingAnnouncement />
      <CardSkeleton />
      <CardSkeleton />
    </div>
  );
}

function RequireAuth({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  const location = useLocation();

  if (status === 'restoring') return <RouteFallback />;
  if (status === 'anonymous') {
    // `state` so the child lands where they were headed after signing in.
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  return <>{children}</>;
}

function RequireProfile({ children }: { children: ReactNode }) {
  const { isParent } = useAuth();
  const { isLoading, missingProfile } = useProfile();

  // A parent has no student profile of their own, and should not be sent to
  // a child's setup screen.
  if (isParent) return <>{children}</>;
  if (isLoading) return <RouteFallback />;
  if (missingProfile) return <Navigate to="/welcome" replace />;
  return <>{children}</>;
}

/**
 * The one old parent URL that carried a parameter.
 *
 * `/parent/students/:id` is now `/progress/:id`, and a bare redirect would
 * drop the id and land a parent on a progress screen for nobody.
 */
function OldProgressLink() {
  const { studentId } = useParams<{ studentId: string }>();
  return <Navigate to={studentId ? `/progress/${studentId}` : '/profile'} replace />;
}

/** Both guards, in the order a first-run child meets them. */
function Protected({ children }: { children: ReactNode }) {
  return (
    <RequireAuth>
      <RequireProfile>{children}</RequireProfile>
    </RequireAuth>
  );
}

export function AppRoutes() {
  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        {/* public */}
        <Route path="/login" element={<WelcomePage />} />
        {/*
          Where a magic link lands. `MAGIC_LINK_PATH` is not ours to rename:
          Meta has approved the template's URL prefix, and the token is
          appended to that. The legacy path renders the same component so a
          link already sitting in someone's WhatsApp still works.
        */}
        <Route path={MAGIC_LINK_PATH} element={<ExchangePage />} />
        <Route path={LEGACY_MAGIC_LINK_PATH} element={<ExchangePage />} />

        {/*
          Policies, and public on purpose — not merely unguarded by accident.

          A parent deciding whether to hand over their child's homework has
          not signed in yet, and the payment gateway and Meta both fetch these
          URLs with no session at all. Putting them behind `RequireAuth` would
          be the kind of mistake that passes every test and fails the one
          reader who matters.
        */}
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/refunds" element={<RefundsPage />} />

        {/* authenticated, profile not yet required */}
        <Route
          path="/welcome"
          element={
            <RequireAuth>
              <ProfileSetupPage />
            </RequireAuth>
          }
        />

        {/*
          Where signing in lands, and the only place an identity is chosen.
          Outside `RequireProfile` because a parent has no student profile and
          never will. Invisible to a one-child family: it opens their child
          and redirects, which is four of five accounts.
        */}
        <Route path="/start" element={<RequireAuth><StartPage /></RequireAuth>} />
        {/* Reachable while blocked: a parent whose trial ended must be able
            to get to the thing that unblocks them. */}
        <Route path="/subscribe" element={<RequireAuth><SubscribePage /></RequireAuth>} />

        {/*
          First run, and the way back to both of these from inside the child's
          app. `RequireAuth` rather than `RequireParent`: after the first child
          is added the device holds a *child's* session for good, and these
          screens render in it and ask for the PIN when they submit. Guarding
          them on the parent role would have made the one template impossible.
        */}
        <Route path="/setup" element={<RequireAuth><ParentSetupPage /></RequireAuth>} />
        <Route path="/children/new" element={<RequireAuth><AddChildPage /></RequireAuth>} />
        <Route
          path="/progress/:studentId"
          element={<RequireAuth><ChildProgressPage /></RequireAuth>}
        />

        {/* child app */}
        <Route path="/" element={<Protected><HomePage /></Protected>} />
        <Route path="/homework" element={<Protected><HomeworkPage /></Protected>} />
        <Route path="/homework/:submissionId" element={<Protected><HomeworkSessionPage /></Protected>} />
        <Route path="/learn" element={<Protected><LearnPage /></Protected>} />
        <Route path="/learn/:skillId" element={<Protected><LearnSkillPage /></Protected>} />
        <Route path="/practice" element={<Protected><PracticePage /></Protected>} />
        <Route path="/practice/:sessionId" element={<Protected><PracticeSessionPage /></Protected>} />
        <Route path="/profile" element={<Protected><ProfilePage /></Protected>} />
        <Route path="/profile/progress" element={<Protected><ProgressPage /></Protected>} />

        {/*
          The parent zone is gone, and these keep the URLs that pointed at it
          working. A PWA on a phone has the old ones in its history and on its
          home screen, and the service worker will happily serve the shell for
          a route that no longer exists -- so without these a parent who
          reopened the app from an old link got the not-found page.
        */}
        <Route path="/profiles" element={<Navigate to="/start" replace />} />
        <Route path="/parent/unlock" element={<Navigate to="/profile" replace />} />
        <Route path="/parent/setup" element={<Navigate to="/setup" replace />} />
        <Route path="/parent/children/new" element={<Navigate to="/children/new" replace />} />
        <Route path="/parent/students/:studentId" element={<OldProgressLink />} />
        <Route path="/parent" element={<Navigate to="/profile" replace />} />

        {/* §12: friendly recovery, and no hint about what does or does not exist */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}
